import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useGetAllBuyerTransactionsQuery } from 'services/api/transactions';
import { setPage } from 'services/transactions/transactionsSlice';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { useInfiniteScroll } from 'pages/Admin/AuctionDetailNew/hooks/useInfiniteScroll';
import TransactionRecordCard from '../components/TransactionRecordCard';
import MobileTransactionCard from '../components/MobileTransactionCard';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoadMoreButton = styled.button`
  margin: 16px auto;
  display: block;
  padding: 10px 32px;
  background: #005a96;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #666;
  font-size: 16px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const toApiFilters = (filters: Record<string, any>) => {
  const result: Record<string, any> = {};
  if (filters.keyword) result.keyword = filters.keyword;
  if (filters.vin) result.vin = filters.vin;
  if (filters.seller_ids?.length) result.seller_ids = filters.seller_ids;
  if (filters.status?.length) result.status = filters.status;
  return result;
};

const AllTab = () => {
  const { page, perPage, filters } = useSelector((state: any) => state.transactions.all);
  const { data, isFetching } = useGetAllBuyerTransactionsQuery({ limit: perPage, offset: page, ...toApiFilters(filters) });
  const dispatch = useDispatch();
  const { isMobile } = useDeviceInfo();

  const transactions = useMemo(() => data?.rows?.ids?.map((id: any) => data?.rows?.entities?.[id]) ?? [], [data]);
  const transactionsCount = useMemo(() => data?.count ?? 0, [data]);

  const loadMore = () => {
    if (perPage * page >= transactionsCount) return;
    dispatch(setPage({ page: page + 1, tab: 'all' }));
  };

  const canLoadMore = perPage * page < transactionsCount;

  const { sentinelRef } = useInfiniteScroll({
    loading: isFetching,
    hasMore: canLoadMore,
    onLoadMore: loadMore,
  });

  if (!isFetching && transactions.length === 0) {
    return <EmptyState>No transactions found.</EmptyState>;
  }

  const CardComponent = isMobile ? MobileTransactionCard : TransactionRecordCard;

  return (
    <>
      <ListWrapper>
        {transactions.map((vehicle: any) => (
          <CardComponent
            key={vehicle.id}
            vehicle={vehicle}
            queryEndpoint="getAllBuyerTransactions"
            reduxTab="all"
          />
        ))}
      </ListWrapper>
      {isMobile ? (
        <LoaderWrapper>
          {isFetching && <CircularProgress size={32} />}
          <Sentinel ref={sentinelRef} />
        </LoaderWrapper>
      ) : (
        canLoadMore && (
          <LoadMoreButton onClick={loadMore} disabled={isFetching}>
            {isFetching ? 'Loading...' : 'Load More'}
          </LoadMoreButton>
        )
      )}
    </>
  );
};

export default AllTab;
